import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ArAgingResponse,
  CreateInvoiceInput,
  CustomerCenterResponse,
  Invoice,
  InvoiceWithDetails,
  ListInvoicesQuery,
  ReceiveCustomerPaymentInput,
  ReceiveCustomerPaymentResult,
  RecordInvoicePaymentInput,
  UpdateInvoiceInput,
} from '@accounting/shared';
import { api } from '../lib/api';

export const invoicesKey = (companyId: string | null, filters: Partial<ListInvoicesQuery> = {}) =>
  ['invoices', companyId ?? 'none', filters.customerId ?? 'all', filters.status ?? 'any'] as const;

export function useInvoices(companyId: string | null, filters: Partial<ListInvoicesQuery> = {}) {
  return useQuery({
    queryKey: invoicesKey(companyId, filters),
    enabled: companyId !== null,
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.customerId) params.set('customerId', filters.customerId);
      if (filters.status) params.set('status', filters.status);
      const qs = params.toString();
      return api
        .get<{ invoices: Invoice[] }>(`/companies/${companyId}/invoices${qs ? '?' + qs : ''}`)
        .then((r) => r.invoices);
    },
  });
}

export function useInvoice(companyId: string | null, id: string) {
  return useQuery({
    queryKey: ['invoice', companyId ?? 'none', id],
    enabled: companyId !== null && !!id,
    queryFn: () =>
      api.get<{ invoice: InvoiceWithDetails }>(`/companies/${companyId}/invoices/${id}`).then((r) => r.invoice),
  });
}

export function useNextInvoiceNumber(companyId: string | null) {
  return useQuery({
    queryKey: ['invoice-next-number', companyId ?? 'none'],
    enabled: companyId !== null,
    queryFn: () =>
      api
        .get<{ invoiceNumber: string }>(`/companies/${companyId}/invoices/next-number`)
        .then((r) => r.invoiceNumber),
  });
}

export function useCustomerCenter(companyId: string | null) {
  return useQuery({
    queryKey: ['customer-center', companyId ?? 'none'],
    enabled: companyId !== null,
    queryFn: () => api.get<CustomerCenterResponse>(`/companies/${companyId}/customer-center`),
  });
}

export function useArAging(companyId: string | null, asOf: string) {
  return useQuery({
    queryKey: ['ar-aging', companyId ?? 'none', asOf],
    enabled: companyId !== null,
    queryFn: () =>
      api.get<ArAgingResponse>(`/companies/${companyId}/reports/ar-aging`, { asOf }),
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, companyId: string | null) {
  const c = companyId ?? 'none';
  qc.invalidateQueries({ queryKey: ['invoices', c] });
  qc.invalidateQueries({ queryKey: ['invoice', c] });
  qc.invalidateQueries({ queryKey: ['invoice-next-number', c] });
  qc.invalidateQueries({ queryKey: ['customer-center', c] });
  qc.invalidateQueries({ queryKey: ['ar-aging', c] });
  qc.invalidateQueries({ queryKey: ['journal-entries', c] });
  qc.invalidateQueries({ queryKey: ['accounts-balances', c] });
  qc.invalidateQueries({ queryKey: ['report'] });
}

export function useCreateInvoice(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInvoiceInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ invoice: InvoiceWithDetails }>(`/companies/${companyId}/invoices`, input)
        .then((r) => r.invoice);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useUpdateInvoice(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateInvoiceInput }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .patch<{ invoice: InvoiceWithDetails }>(`/companies/${companyId}/invoices/${id}`, input)
        .then((r) => r.invoice);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useIssueInvoice(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ invoice: InvoiceWithDetails }>(`/companies/${companyId}/invoices/${id}/issue`, {})
        .then((r) => r.invoice);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useRecordInvoicePayment(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: RecordInvoicePaymentInput }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ invoice: InvoiceWithDetails }>(`/companies/${companyId}/invoices/${id}/payments`, input)
        .then((r) => r.invoice);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useReceiveCustomerPayment(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReceiveCustomerPaymentInput) => {
      if (!companyId) throw new Error('No active company');
      return api.post<ReceiveCustomerPaymentResult>(`/companies/${companyId}/customer-payments`, input);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useRemoveInvoicePayment(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, paymentId }: { invoiceId: string; paymentId: string }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .delete<{ invoice: InvoiceWithDetails }>(`/companies/${companyId}/invoices/${invoiceId}/payments/${paymentId}`)
        .then((r) => r.invoice);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useVoidInvoice(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ invoice: Invoice }>(`/companies/${companyId}/invoices/${id}/void`, { reason });
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}
